import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

/**
 * Button style variants. `variant` sets intent (primary / destructive / quiet)
 * and `size` sets density. Consumers compose these via the `Button` component;
 * extra classes passed through `className` win over the base set (see `cn`).
 */
const buttonVariants = cva(
  // No focus ring utilities here: the design system defines one quiet focus
  // treatment (brand border + soft halo) globally on `:focus-visible` in
  // index.css. Re-declaring `ring-2 ring-offset-2` per-component is what
  // produces the hard double ring the system explicitly rejects.
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-brand text-primary-foreground hover:bg-brand-hover',
        destructive: 'bg-danger text-destructive-foreground hover:bg-danger-hover',
        outline:
          'border border-default bg-surface hover:border-strong hover:bg-subtle',
        secondary: 'bg-subtle text-primary-text hover:bg-muted',
        ghost: 'text-secondary-text hover:bg-subtle hover:text-primary-text',
      },
      // Heights follow the design system's control sizes (32/28/36px) rather
      // than shadcn's roomier 40px default — this is a dense, data-first app.
      size: {
        default: 'h-8 px-3',
        sm: 'h-7 px-2.5 text-xs',
        lg: 'h-9 px-5',
        icon: 'h-8 w-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render the button's styles onto its single child instead of a `<button>`. */
  asChild?: boolean
}

/**
 * A styled button. Pass `asChild` to apply button styling to a child element
 * (e.g. a router `<Link>`) via Radix `Slot`.
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button({ className, variant, size, asChild = false, ...props }, ref) {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
      />
    )
  },
)
